@if (isOpen()) {
  <div
    class="fixed inset-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4 animate-fade-in"
    (click)="onBackdropClick($event)"
    (keydown.enter)="onBackdropClick($event)"
    (keydown.space)="onBackdropClick($event)"
  >
    <dialog
      open
      class="bg-white rounded-xl w-full max-w-[600px] max-h-[90vh] flex flex-col shadow-lg animate-slide-up m-0 p-0 border-0"
      [attr.aria-label]="modalTitle"
      (click)="$event.stopPropagation()"
      (keydown.escape)="onCancel()"
    >
      <div
        class="flex justify-between items-center px-6 py-3 border-b border-gray-300"
      >
        <h3 class="text-lg font-bold text-brand-tertiary m-0">
          {{ modalTitle }}
        </h3>
        @if (showCloseButton) {
          <button
            class="w-8 h-8 min-w-[2rem] min-h-[2rem] border-none bg-transparent cursor-pointer p-0 flex items-center justify-center transition-opacity duration-200 hover:opacity-70 focus-visible:outline-2 focus-visible:outline-brand-tertiary focus-visible:outline-offset-2 focus-visible:rounded"
            (click)="onCancel()"
            aria-label="Cerrar modal"
            type="button"
          >
            <span class="icon-equis w-6 h-6 block" aria-hidden="true"></span>
          </button>
        }
      </div>

      <div class="px-6 py-3 flex flex-col gap-2 overflow-y-auto flex-1">
        @if (state() === FinalSaveModalState.CONFIRMATION) {
          <div class="flex flex-col gap-2">
            <div class="flex flex-col items-center gap-1 text-center">
              <span
                class="icon-alerta w-12 h-12 block text-orange-500"
                aria-hidden="true"
              ></span>
              <p class="text-base leading-6 text-gray-700 m-0 font-medium">
                {{ modalMessage }}
              </p>
            </div>

            @if (selectedSamples().length > 0) {
              <div class="border border-gray-300 rounded-lg overflow-hidden">
                <div class="bg-gray-100 px-4 py-2 border-b border-gray-300">
                  <h4 class="text-xs font-semibold text-gray-700 m-0">
                    Muestras seleccionadas ({{ selectedCount() }})
                  </h4>
                </div>
                <div class="max-h-[200px] overflow-y-auto">
                  <table class="w-full">
                    <caption class="sr-only">
                      Lista de muestras seleccionadas para guardar definitivo
                    </caption>
                    <thead class="bg-gray-50 sticky top-0">
                      <tr>
                        <th
                          scope="col"
                          class="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase"
                        >
                          ID Muestra
                        </th>
                        <th
                          scope="col"
                          class="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase"
                        >
                          Paciente
                        </th>
                        <th
                          scope="col"
                          class="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase"
                        >
                          Examen
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (sample of selectedSamples(); track sample.id) {
                        <tr class="border-t border-gray-200 hover:bg-gray-50">
                          <td class="px-4 py-2 text-sm text-gray-900">
                            {{ sample.specimenId }}
                          </td>
                          <td class="px-4 py-2 text-sm text-gray-900">
                            {{ sample.patientFirstName }}
                            {{ sample.patientLastName }}
                          </td>
                          <td class="px-4 py-2 text-sm text-gray-900">
                            {{ sample.examName }}
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }

            <div
              class="bg-orange-50 border border-orange-200 rounded-lg p-4"
              role="alert"
              aria-live="polite"
            >
              <p class="text-sm text-orange-800 m-0">
                <strong class="font-semibold">Advertencia:</strong> Esta acción
                no se puede deshacer. Las muestras pasarán al módulo de
                laboratorio.
              </p>
            </div>
          </div>
        }

        @if (state() === FinalSaveModalState.PROCESSING) {
          <div class="flex flex-col items-center gap-2 py-2">
            <output class="spinner w-12 h-12 relative block" aria-live="polite">
              <div
                class="spinner-circle w-full h-full border-[3px] border-gray-300 border-t-brand-tertiary rounded-full"
              ></div>
            </output>
            <p class="text-base text-gray-700 m-0">{{ modalMessage }}</p>
          </div>
        }

        @if (state() === FinalSaveModalState.SUCCESS) {
          <div class="flex flex-col gap-4">
            <div class="flex flex-col items-center gap-3 text-center">
              <span
                class="icon-success w-12 h-12 block text-green-500"
                aria-hidden="true"
              ></span>
              <p class="text-base leading-6 text-gray-700 m-0 font-medium">
                {{ modalMessage }}
              </p>
            </div>

            @if (showSuccessDetails) {
              <section
                class="bg-green-50 border border-green-200 rounded-lg p-4"
                aria-label="Detalles de la operación"
              >
                <div class="flex flex-col gap-2 text-sm text-green-800">
                  <p class="m-0">
                    <strong class="font-semibold">Fecha y hora:</strong>
                    {{ formattedTimestamp }}
                  </p>
                  @if (response()?.username) {
                    <p class="m-0">
                      <strong class="font-semibold">Usuario:</strong>
                      {{ response()?.username }}
                    </p>
                  }
                </div>
              </section>
            }

            <output
              class="bg-blue-50 border border-blue-200 rounded-lg p-4 block"
              aria-live="polite"
            >
              <p class="text-sm text-blue-800 m-0">
                <span aria-hidden="true">ℹ</span>
                <span class="sr-only">Información:</span>
                Las muestras están ahora disponibles en el módulo de
                laboratorio.
              </p>
            </output>
          </div>
        }
      </div>

      @if (showButtons) {
        <div class="flex justify-end gap-4 px-6 py-3 border-t border-gray-300">
          <app-button
            label="Cancelar"
            [type]="ButtonTypeEnum.PRIMARY_OUTLINE"
            [size]="ButtonSizeEnum.MD"
            (handleClick)="onCancel()"
          >
          </app-button>
          <app-button
            label="Confirmar"
            [type]="ButtonTypeEnum.PRIMARY"
            [size]="ButtonSizeEnum.MD"
            (handleClick)="onConfirm()"
          >
          </app-button>
        </div>
      } @else if (state() === FinalSaveModalState.SUCCESS) {
        <div class="flex justify-center px-6 py-3 border-t border-gray-300">
          <app-button
            label="Aceptar"
            [type]="ButtonTypeEnum.PRIMARY"
            [size]="ButtonSizeEnum.MD"
            (handleClick)="onCancel()"
          >
          </app-button>
        </div>
      }
    </dialog>
  </div>
}
